'use client';

import { Facebook, Linkedin, Instagram, Youtube } from 'lucide-react';
import { resolveMediaUrl, APP_URL } from '@/lib/api';

// V1.25.938 — Suppression TOTALE du refetch client. Le footer se base 100%
// sur les props server-rendered. Plus de useEffect/setState au mount → plus
// de re-render → les <a> ne sont JAMAIS demontes apres hydratation → clics
// fiables au 1er coup. L'auto-rebuild watcher (poll 30s) regenere la vitrine
// quand admin edite, donc les changements apparaissent au prochain refresh.
export default function Footer({ settings, initialCmsPages = [] }) {
  const cmsPages = initialCmsPages;

  const logoUrl = resolveMediaUrl(settings?.logo_url);
  const social = settings?.social_links || {};
  const year = new Date().getFullYear();
  const footerCfg = settings?.footer || {};

  // Helper : ancre (#xxx) → /xxx pour toujours pointer vers la home
  // (depuis une page CMS, #features seul ne marcherait pas).
  const normalizeUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('#')) return '/' + url;
    return url;
  };

  // Reouvre le bandeau de consentement cookies (retrait du consentement,
  // exigence RGPD). Branche sur CookieConsent via window.__lbOpenCookiePrefs
  // ou l'evenement custom si le composant n'est pas encore monte.
  const openCookiePrefs = () => {
    if (typeof window === 'undefined') return;
    if (typeof window.__lbOpenCookiePrefs === 'function') {
      window.__lbOpenCookiePrefs();
    } else {
      window.dispatchEvent(new Event('ligobox:open-cookie-prefs'));
    }
  };

  // Colonnes configurées depuis l'admin (avec fallback par défaut si vide).
  const customColumns = Array.isArray(footerCfg.columns) ? footerCfg.columns : [];
  const fallbackColumns = [
    { title: 'Produit', items: [
      { label: 'Fonctionnalites', url: '/#features', target: '_self' },
      { label: 'Tarifs', url: '/#packs', target: '_self' },
    ]},
    { title: 'Entreprise', items: [
      { label: 'Contact', url: '/#contact', target: '_self' },
    ]},
  ];
  const columns = customColumns.length ? customColumns : fallbackColumns;

  // Pages CMS legal (CGV, mentions, etc.) — toujours regroupées dans une colonne dédiée.
  const legalPages = cmsPages.filter(p => p.page_type === 'legal');
  const otherPages = cmsPages.filter(p => p.page_type !== 'legal');

  const showLogo  = footerCfg.show_logo !== false;
  const showSocial = footerCfg.show_social_icons !== false;
  const showEmail = footerCfg.show_email !== false;
  const tagline   = footerCfg.tagline || settings?.site_tagline || '';
  const copyright = footerCfg.copyright_text
    || `© ${year} ${settings?.site_title || 'Ligobox'}. Tous droits reserves.`;

  const hasSocial = social.facebook || social.linkedin || social.instagram || social.youtube;

  return (
    <footer style={{
      background: '#1a1a1a',
      color: '#e5e7eb',
      padding: '60px 0 30px',
    }}>
      <div className="container-page">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '40px',
          marginBottom: '40px',
        }}>
          {/* Colonne identité (logo + tagline) */}
          {showLogo && (
            <div>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={settings?.site_title || 'Ligobox'}
                  style={{ height: '36px', marginBottom: '16px', filter: 'brightness(0) invert(1)' }}
                />
              ) : (
                <div style={{ fontSize: '22px', fontWeight: 800, marginBottom: '16px', color: '#fff' }}>
                  {settings?.site_title || 'Ligobox'}
                </div>
              )}
              {tagline && (
                <p style={{ fontSize: '14px', color: '#9ca3af', lineHeight: 1.6, margin: 0 }}>
                  {tagline}
                </p>
              )}
            </div>
          )}

          {/* Colonnes configurées via l'admin (titre + items) */}
          {columns.map((col, idx) => (
            <div key={idx}>
              <h4 style={footerH4Style}>{col.title || ''}</h4>
              <ul style={footerListStyle}>
                {(col.items || []).map((it, i) => {
                  // Mode TEXTE : bloc texte stylé (taille, alignement, couleur)
                  // Reset list-item styling (textIndent, paddingInlineStart) qui
                  // peut creer une indentation parasite sur la 1ere ligne. On
                  // normalise aussi les whitespaces : trim global + suppression
                  // des espaces/tabs en debut de chaque ligne (pre-wrap les
                  // preserverait sinon, donnant l'effet "1ere ligne decalee").
                  if (it.kind === 'text') {
                    const cleaned = (it.text || '')
                      .replace(/^\s+/, '')              // trim global du debut
                      .split('\n')
                      .map(line => line.replace(/^[\t ]+/, '')) // strip leading per-line
                      .join('\n');
                    return (
                      <li key={i} style={{
                        fontSize: (it.font_size || 14) + 'px',
                        color: it.color || '#9ca3af',
                        textAlign: it.text_align || 'left',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.5,
                        textIndent: 0,
                        paddingInlineStart: 0,
                        marginInlineStart: 0,
                        listStyle: 'none',
                      }}>
                        {cleaned}
                      </li>
                    );
                  }
                  // Mode LINK (défaut)
                  return (
                    <li key={i}>
                      <a
                        href={normalizeUrl(it.url)}
                        target={it.target || '_self'}
                        rel={it.target === '_blank' ? 'noopener noreferrer' : undefined}
                        style={footerLinkStyle}
                      >
                        {it.label || ''}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* Pages CMS non-légales (auto-ajoutées si show_in_footer) */}
          {otherPages.length > 0 && (
            <div>
              <h4 style={footerH4Style}>Plus</h4>
              <ul style={footerListStyle}>
                {otherPages.map(page => (
                  <li key={page.id}>
                    <a href={`/${page.slug}`} style={footerLinkStyle}>{page.title}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Pages légales (auto-regroupées) */}
          {legalPages.length > 0 && (
            <div>
              <h4 style={footerH4Style}>Légal</h4>
              <ul style={footerListStyle}>
                {legalPages.map(page => (
                  <li key={page.id}>
                    <a href={`/${page.slug}`} style={footerLinkStyle}>{page.title}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Réseaux sociaux + email */}
          {(showSocial || showEmail) && (hasSocial || (showEmail && settings?.contact?.email_destinataire)) && (
            <div>
              <h4 style={footerH4Style}>Suivez-nous</h4>
              {showSocial && hasSocial && (
                <div style={{ display: 'flex', gap: '12px' }}>
                  {social.facebook && (
                    <a href={social.facebook} target="_blank" rel="noopener noreferrer" style={socialIconStyle}>
                      <Facebook size={18} />
                    </a>
                  )}
                  {social.linkedin && (
                    <a href={social.linkedin} target="_blank" rel="noopener noreferrer" style={socialIconStyle}>
                      <Linkedin size={18} />
                    </a>
                  )}
                  {social.instagram && (
                    <a href={social.instagram} target="_blank" rel="noopener noreferrer" style={socialIconStyle}>
                      <Instagram size={18} />
                    </a>
                  )}
                  {social.youtube && (
                    <a href={social.youtube} target="_blank" rel="noopener noreferrer" style={socialIconStyle} aria-label="YouTube">
                      <Youtube size={18} />
                    </a>
                  )}
                </div>
              )}
              {showEmail && settings?.contact?.email_destinataire && (
                <p style={{ marginTop: '16px', fontSize: '14px', color: '#9ca3af' }}>
                  <a href={`mailto:${settings.contact.email_destinataire}`} style={{ color: '#9ca3af' }}>
                    {settings.contact.email_destinataire}
                  </a>
                </p>
              )}
            </div>
          )}
        </div>

        <div style={{
          paddingTop: '24px',
          borderTop: '1px solid #2d2d2d',
          fontSize: '13px',
          color: '#6b7280',
          textAlign: 'center',
        }}>
          {copyright}
          <span style={{ margin: '0 8px', opacity: 0.5 }}>·</span>
          <button
            type="button"
            onClick={openCookiePrefs}
            style={{
              background: 'none',
              border: 'none',
              color: '#9ca3af',
              fontSize: '13px',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
              fontFamily: 'inherit',
            }}
          >
            Gérer les cookies
          </button>
        </div>
      </div>
    </footer>
  );
}

const footerH4Style = {
  fontSize: '14px', fontWeight: 700, color: '#fff',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  margin: '0 0 16px',
};
const footerListStyle = {
  listStyle: 'none', padding: 0, margin: 0,
  display: 'flex', flexDirection: 'column', gap: '10px',
};
const footerLinkStyle = { color: '#9ca3af', fontSize: '14px', textDecoration: 'none' };

const socialIconStyle = {
  width: '40px', height: '40px',
  borderRadius: '8px',
  background: '#2d2d2d',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#e5e7eb',
  transition: 'background 0.15s',
};
